import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    dialogTitle: string;
    dialogCloseLabel: string;
    noResults: string;
    placeholder: string;
    or: string;
    resetSection: string;
    resetSectionBlocked: string;
    resetConfirmation: {
        /** Title for reset section confirmation dialog */
        title: string;
        /** Details for reset section confirmation dialog */
        details: (category: string) => string | React.ReactNode;
        /** Confirm button label for reset section dialog */
        confirm: string;
        /** Cancel button label for reset section dialog */
        cancel: string;
    };
    actionModifiers: {
        /** Action modifier label for mouse click shortcuts */
        click: string;
        /** Action modifier label for mouse drag shortcuts */
        drag: string;
        /** Action modifier label for press-and-hold shortcuts */
        hold: string;
    };
    customized: {
        /** Tooltip describing a customized shortcut */
        tooltip: string;
        /** Chip label for customized shortcut */
        chip: string;
        /** Tooltip for the revert button inside the Customized pill */
        revertTooltip: string;
    };
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            /** Dialog title for keyboard shortcuts */
            dialogTitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.dialogTitle?c=2lrchmyyfvb5x", defaultMessage: "Keyboard Shortcuts" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Dialog close label for keyboard shortcuts */
            dialogCloseLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.dialogCloseLabel?c=21e0ywujs0zuu", defaultMessage: "Close keyboard shortcuts dialog" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Empty state text when no shortcuts match the search */
            noResults: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.noResults?c=2cteahvyfroz8", defaultMessage: "No matches found." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Search input placeholder for keyboard shortcuts */
            placeholder: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.placeholder?c=2nxobf2521ml7", defaultMessage: "Search keyboard shortcuts\u2026" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Separator label between primary and alternative shortcuts */
            or: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.or?c=ugh9gpt2eo3u", defaultMessage: "or" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Button label to reset all customized shortcuts within a category */
            resetSection: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.resetSection?c=3t5uy051wu4kh", defaultMessage: "Reset section customizations" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip shown when the section reset button is disabled because one or
             *  more of its shortcuts would conflict if reverted to default. */
            resetSectionBlocked: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.resetSectionBlocked?c=1k9r5mkn3yhay", defaultMessage: "One or more shortcuts in this section can''t be reset due to conflicts" }));
                },
                enumerable: true,
                configurable: true
            },
            resetConfirmation: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Title for reset section confirmation dialog */
                        title: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.resetConfirmation.title?c=199viy200lmy0", defaultMessage: "Reset Section Customizations" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Details for reset section confirmation dialog */
                        details: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (category: string) => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.resetConfirmation.details?c=322h796xvf7xs", defaultMessage: "Are you sure you want to reset all \"{category}\" customizations? This action cannot be undone." }, { category: category }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Confirm button label for reset section dialog */
                        confirm: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.resetConfirmation.confirm?c=37nyxuspm9dfs", defaultMessage: "Reset Section" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Cancel button label for reset section dialog */
                        cancel: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.resetConfirmation.cancel?c=2ojhd8rzicmo0", defaultMessage: "Cancel" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            actionModifiers: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Action modifier label for mouse click shortcuts */
                        click: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.actionModifiers.click?c=2h0fjj8t5tj2g", defaultMessage: "Click" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Action modifier label for mouse drag shortcuts */
                        drag: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.actionModifiers.drag?c=3fzjk6370fbjf", defaultMessage: "Drag" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Action modifier label for press-and-hold shortcuts */
                        hold: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.actionModifiers.hold?c=1xxzjjeqscmm9", defaultMessage: "Hold" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            },
            customized: {
                get() {
                    return Object.defineProperties({
                        [__intl__.BOUND_INTL]: this[__intl__.BOUND_INTL] ?? __intl__.getIntl()
                    }, {
                        /** Tooltip describing a customized shortcut */
                        tooltip: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.customized.tooltip?c=zeoale9xq58s", defaultMessage: "This shortcut has been customized and can be modified in your settings" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Chip label for customized shortcut */
                        chip: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.customized.chip?c=33lqbjh49ky0e", defaultMessage: "Customized" }));
                            },
                            enumerable: true,
                            configurable: true
                        },
                        /** Tooltip for the revert button inside the Customized pill */
                        revertTooltip: {
                            get() {
                                return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "CommandRegistryHelpDialog.customized.revertTooltip?c=1j301zmnau01i", defaultMessage: "Reset shortcut to default" }));
                            },
                            enumerable: true,
                            configurable: true
                        }
                    });
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
